import type {
  AgentCapability,
  NodeWatchKind,
  NodeWatchView,
  NotificationChannelView,
  ResourcePath,
} from '@occ/contracts';
import { useCan } from '@refinedev/core';
import { Box, ChevronDown, Cpu, Eye, Pause, Play, Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { describeHttpError } from '../api/http';
import {
  fetchNodeWatches,
  loadCandidates,
  removeNodeWatch,
  setNodeWatch,
  type WatchCandidate,
} from '../data/node-watch';
import { timeAgo } from '../lib/format';
import { ChannelPicker, useNotificationChannels } from './ChannelPicker';
import { Badge, Button, Card, EmptyState, Field, Loading, Toasts, useToasts } from './ui';

const KIND_LABEL: Record<NodeWatchKind, string> = {
  process: 'Procesos',
  container: 'Contenedores',
};

/**
 * Procesos y contenedores vigilados del nodo (M16): "avísame si esto lleva cinco
 * minutos sin correr".
 *
 * Es el hermano de los avisos de la tarjeta de al lado y reutiliza el mismo motor —
 * una regla de alerta de ámbito nodo por cada cosa vigilada—, así que los avisos
 * salen por los mismos canales, con la misma agrupación y repetición. Lo único
 * propio es que aquí hay muchas por nodo y se eligen de lo que corre ahora.
 *
 * Se vigila **por nombre**, no por PID: el PID cambia en cada reinicio, y una
 * vigilancia atada a él no distinguiría "se cayó" de "se reinició bien". El PID se
 * enseña en el selector solo para reconocer el proceso.
 */
export function NodeWatchesCard({
  nodeId,
  path,
  online,
  capabilities,
}: {
  nodeId: string;
  path: ResourcePath;
  online: boolean;
  capabilities: AgentCapability[];
}) {
  const { data: permission } = useCan({
    resource: 'node-notify',
    action: 'edit',
    params: { path },
  });
  const channels = useNotificationChannels();
  const toasts = useToasts();

  const [watches, setWatches] = useState<NodeWatchView[] | null>(null);
  const [adding, setAdding] = useState(false);

  const canEdit = permission?.can ?? false;
  const reason = !canEdit ? (permission?.reason ?? 'Sin permiso') : undefined;

  const load = useCallback(async () => {
    try {
      setWatches(await fetchNodeWatches(nodeId));
    } catch {
      // Sin permiso o sin backend: la tarjeta se queda vacía y el motivo va en el
      // botón. Mostrar un error aquí taparía el resto de la ficha por algo accesorio.
      setWatches([]);
    }
  }, [nodeId]);

  useEffect(() => {
    void load();
    // Los estados cambian con el barrido, cada minuto: sin esto la pantalla
    // enseñaría "pendiente" hasta que el operador recargara.
    const timer = setInterval(() => void load(), 30_000);
    return () => clearInterval(timer);
  }, [load]);

  const run = async (work: () => Promise<unknown>, success: string) => {
    try {
      await work();
      toasts.ok(success);
      await load();
      return true;
    } catch (cause) {
      toasts.fail(describeHttpError(cause));
      return false;
    }
  };

  return (
    <Card
      title={
        <span className="inline-flex items-center gap-2">
          <Eye className="size-4 opacity-70" />
          Procesos y contenedores vigilados
        </span>
      }
      actions={
        <Button
          size="sm"
          variant="secondary"
          disabled={!canEdit}
          title={reason}
          onClick={() => setAdding((value) => !value)}
        >
          <Plus className="size-3.5" />
          Vigilar
        </Button>
      }
    >
      <Toasts toasts={toasts.toasts} />
      <div className="grid gap-3">
        <p className="text-xs opacity-60">
          Avisa si lo vigilado lleva 5 minutos seguidos sin correr. Un reinicio o un redespliegue
          rápido no dispara nada.
        </p>

        {adding && (
          <AddWatch
            nodeId={nodeId}
            online={online}
            capabilities={capabilities}
            channels={channels}
            existing={watches ?? []}
            onSubmit={async (request) => {
              const ok = await run(() => setNodeWatch(nodeId, request), 'Vigilancia activada.');
              if (ok) setAdding(false);
            }}
            onCancel={() => setAdding(false)}
          />
        )}

        {watches === null ? (
          <Loading label="Cargando vigilancias…" />
        ) : watches.length === 0 ? (
          !adding && (
            <EmptyState title="Este nodo no vigila ningún proceso ni contenedor.">
              Pulsa «Vigilar» para elegir uno de lo que está corriendo.
            </EmptyState>
          )
        ) : (
          <ul className="divide-y divide-black/5 dark:divide-white/5">
            {watches.map((watch) => (
              <WatchRow
                key={watch.ruleId}
                watch={watch}
                channels={channels}
                canEdit={canEdit}
                onToggle={() =>
                  run(
                    () =>
                      setNodeWatch(nodeId, {
                        kind: watch.kind,
                        target: watch.target,
                        enabled: !watch.enabled,
                        channelIds: watch.channelIds,
                      }),
                    watch.enabled ? 'Vigilancia pausada.' : 'Vigilancia reanudada.',
                  )
                }
                onSaveChannels={(channelIds) =>
                  run(
                    () =>
                      setNodeWatch(nodeId, {
                        kind: watch.kind,
                        target: watch.target,
                        enabled: watch.enabled,
                        channelIds,
                      }),
                    'Canales actualizados.',
                  )
                }
                onRemove={() =>
                  run(() => removeNodeWatch(nodeId, watch.ruleId), 'Se dejó de vigilar.')
                }
              />
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}

function statusOf(watch: NodeWatchView): { tone: 'neutral' | 'warn' | 'ok'; label: string } {
  if (!watch.enabled) return { tone: 'neutral', label: 'Pausada' };
  switch (watch.status) {
    case 'ok':
      return { tone: 'ok', label: 'Corriendo' };
    case 'pending':
      return {
        tone: 'warn',
        label: `Sin correr ${timeAgo(watch.since).replace(/^hace /, 'desde hace ')}`,
      };
    case 'down':
      return {
        tone: 'warn',
        label: `Caído ${timeAgo(watch.since).replace(/^hace /, 'desde hace ')}`,
      };
    case 'unknown':
      // No es "bien": nadie lo ha comprobado todavía, o el nodo no contestó.
      return { tone: 'neutral', label: 'Sin medir' };
  }
}

function WatchRow({
  watch,
  channels,
  canEdit,
  onToggle,
  onSaveChannels,
  onRemove,
}: {
  watch: NodeWatchView;
  channels: NotificationChannelView[];
  canEdit: boolean;
  onToggle: () => Promise<boolean>;
  onSaveChannels: (channelIds: string[]) => Promise<boolean>;
  onRemove: () => Promise<boolean>;
}) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set(watch.channelIds));
  const [busy, setBusy] = useState(false);
  const status = statusOf(watch);
  const Icon = watch.kind === 'container' ? Box : Cpu;
  const dirty =
    selected.size !== watch.channelIds.length || watch.channelIds.some((id) => !selected.has(id));

  const act = async (work: () => Promise<boolean>) => {
    setBusy(true);
    try {
      await work();
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="grid gap-2 py-2 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-center gap-2">
        <Icon className="size-4 shrink-0 opacity-60" aria-label={KIND_LABEL[watch.kind]} />
        <span className="min-w-0 flex-1 truncate font-mono text-sm" title={watch.target}>
          {watch.target}
        </span>
        <Badge tone={status.tone}>{status.label}</Badge>
        <Button variant="ghost" size="sm" onClick={() => setOpen((value) => !value)}>
          Canales ({watch.channelIds.length})
          <ChevronDown className={`size-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-8"
          disabled={!canEdit || busy}
          title={watch.enabled ? 'Pausar' : 'Reanudar'}
          aria-label={watch.enabled ? 'Pausar' : 'Reanudar'}
          onClick={() => void act(onToggle)}
        >
          {watch.enabled ? <Pause className="size-3.5" /> : <Play className="size-3.5" />}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-8"
          disabled={!canEdit || busy}
          title="Dejar de vigilar"
          aria-label="Dejar de vigilar"
          onClick={() => void act(onRemove)}
        >
          <Trash2 className="size-3.5" />
        </Button>
      </div>

      {open && (
        <div className="grid gap-2 pl-6">
          <ChannelPicker
            channels={channels}
            selected={selected}
            disabled={!canEdit}
            onToggle={(id) =>
              setSelected((current) => {
                const next = new Set(current);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
              })
            }
          />
          {dirty && (
            <Button
              size="sm"
              variant="primary"
              className="w-fit"
              disabled={!canEdit || busy}
              onClick={() => void act(() => onSaveChannels([...selected]))}
            >
              Guardar canales
            </Button>
          )}
        </div>
      )}
    </li>
  );
}

function AddWatch({
  nodeId,
  online,
  capabilities,
  channels,
  existing,
  onSubmit,
  onCancel,
}: {
  nodeId: string;
  online: boolean;
  capabilities: AgentCapability[];
  channels: NotificationChannelView[];
  existing: NodeWatchView[];
  onSubmit: (request: {
    kind: NodeWatchKind;
    target: string;
    enabled: true;
    channelIds: string[];
  }) => Promise<void>;
  onCancel: () => void;
}) {
  const [kind, setKind] = useState<NodeWatchKind>('container');
  const [target, setTarget] = useState('');
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [candidates, setCandidates] = useState<WatchCandidate[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Lista vacía = "el backend no las publica todavía", no "no sabe hacer nada":
  // no se filtra. Mismo criterio que las pestañas de la ficha.
  const containersUnsupported = capabilities.length > 0 && !capabilities.includes('containers');

  useEffect(() => {
    if (kind === 'container' && containersUnsupported) {
      setCandidates([]);
      setLoadError(
        'El agente de este nodo no declara la capacidad «containers»: no hay Docker, o el agente es anterior a esta función.',
      );
      return;
    }
    if (!online) {
      setCandidates([]);
      setLoadError('El nodo no está conectado: escribe el nombre a mano.');
      return;
    }

    let cancelled = false;
    setCandidates(null);
    setLoadError(null);
    loadCandidates(nodeId, kind)
      .then((list) => {
        if (!cancelled) setCandidates(list);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setCandidates([]);
        setLoadError(`${describeHttpError(cause)} — puedes escribir el nombre a mano.`);
      });
    return () => {
      cancelled = true;
    };
  }, [nodeId, kind, online, containersUnsupported]);

  const watched = useMemo(
    () => new Set(existing.filter((watch) => watch.kind === kind).map((watch) => watch.target)),
    [existing, kind],
  );

  const visible = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    return (candidates ?? []).filter(
      (candidate) =>
        needle === '' ||
        candidate.target.toLowerCase().includes(needle) ||
        candidate.detail.toLowerCase().includes(needle),
    );
  }, [candidates, filter]);

  const clean = target.trim();
  const duplicate = watched.has(clean);

  return (
    <div className="grid gap-3 rounded-md bg-accent/30 p-3">
      <div className="flex gap-1.5" role="tablist" aria-label="Qué vigilar">
        {(Object.keys(KIND_LABEL) as NodeWatchKind[]).map((option) => (
          <Button
            key={option}
            size="sm"
            variant={kind === option ? 'primary' : 'secondary'}
            role="tab"
            aria-selected={kind === option}
            onClick={() => {
              setKind(option);
              setTarget('');
              setFilter('');
            }}
          >
            {option === 'container' ? <Box className="size-3.5" /> : <Cpu className="size-3.5" />}
            {KIND_LABEL[option]}
          </Button>
        ))}
      </div>

      <Field
        label="Buscar en lo que corre ahora"
        placeholder="Filtrar por nombre, imagen o PID…"
        value={filter}
        onChange={(event) => setFilter(event.target.value)}
      />

      <div className="max-h-52 overflow-y-auto rounded bg-background/60">
        {candidates === null ? (
          <div className="p-3">
            <Loading label="Leyendo el nodo…" />
          </div>
        ) : loadError ? (
          <p className="p-3 text-xs opacity-70">{loadError}</p>
        ) : visible.length === 0 ? (
          <p className="p-3 text-xs opacity-60">Nada coincide con el filtro.</p>
        ) : (
          <ul>
            {visible.map((candidate) => (
              <li key={`${candidate.kind}:${candidate.target}`}>
                <button
                  type="button"
                  onClick={() => setTarget(candidate.target)}
                  className={[
                    'flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left text-sm hover:bg-accent/60',
                    candidate.target === clean ? 'bg-accent/60' : '',
                  ].join(' ')}
                >
                  <span className="min-w-0 truncate font-mono">{candidate.target}</span>
                  <span className="flex shrink-0 items-center gap-2 text-xs opacity-70">
                    {candidate.kind === 'container' && !candidate.running && (
                      <Badge tone="warn">parado</Badge>
                    )}
                    {watched.has(candidate.target) && <Badge tone="ok">ya vigilado</Badge>}
                    <span className="max-w-[16rem] truncate">{candidate.detail}</span>
                  </span>
                </button>
                {candidate.suggestedPrefix && candidate.target === clean && (
                  <div className="flex items-center gap-2 px-3 pb-1.5 text-xs">
                    <span className="opacity-60">
                      Parece recreado en cada despliegue (Coolify). Si cambia de nombre, esta
                      vigilancia dejaría de encontrarlo.
                    </span>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setTarget(candidate.suggestedPrefix ?? '')}
                    >
                      Vigilar como {candidate.suggestedPrefix}
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <Field
        label="Se vigilará"
        placeholder={kind === 'container' ? 'nombre del contenedor' : 'nombre del proceso'}
        value={target}
        maxLength={60}
        onChange={(event) => setTarget(event.target.value)}
        hint="Por nombre exacto; un * al final lo vuelve un prefijo (por ejemplo abc123-*)."
      />

      <div className="grid gap-1.5">
        <span className="text-xs opacity-60">Canales por los que sale el aviso:</span>
        <ChannelPicker
          channels={channels}
          selected={selected}
          onToggle={(id) =>
            setSelected((current) => {
              const next = new Set(current);
              if (next.has(id)) next.delete(id);
              else next.add(id);
              return next;
            })
          }
        />
      </div>

      <div className="flex items-center justify-end gap-2">
        {duplicate && (
          <span className="mr-auto text-xs opacity-70">
            Ya se vigila: guardar solo actualiza sus canales.
          </span>
        )}
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          size="sm"
          disabled={clean === '' || busy}
          onClick={() => {
            setBusy(true);
            void onSubmit({
              kind,
              target: clean,
              enabled: true,
              channelIds: [...selected],
            }).finally(() => setBusy(false));
          }}
        >
          {busy ? 'Guardando…' : 'Vigilar'}
        </Button>
      </div>
    </div>
  );
}
